import { Box, chakra, forwardRef } from "@chakra-ui/react";
import { TbChevronDown } from "react-icons/tb";
import { SelectHTMLAttributes } from "react";

/**
 * Select — native `<select>` styled to match `.select` in the redesign bundle.
 * design ref: styles.css:833–870
 *
 * Built on a styled native select (no Chakra `Select` variants — those drag in
 * unwanted padding + chevron). Chevron rendered as a `<TbChevronDown>` absolutely
 * positioned over the right padding gap. The CSS uses an inline SVG background;
 * a real icon gives us crisper rendering + theme color.
 */
export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  size?: "sm" | "md";
}

const SIZE_STYLES = {
  md: { px: "md", py: "sm", fontSize: "md" }, // 8px 12px / 14px
  sm: { px: "2.5", py: "1xs", fontSize: "smd" }, // 6px 10px / 13px
} as const;

// Native select with our exact chrome. Padding-right gets bumped to make room
// for the chevron (36px = 12px space + 12px icon + 12px space).
const StyledSelect = chakra("select", {
  baseStyle: {
    width: "100%",
    display: "block",
    appearance: "none",
    fontFamily: "inherit",
    color: "neutral.950",
    lineHeight: "1.4",
    bg: "neutral.25",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "neutral.300",
    borderRadius: "component.lg", // 8px
    boxShadow: "xs",
    cursor: "pointer",
    transition: "border-color 120ms, box-shadow 120ms, background-color 120ms",
    _hover: {
      borderColor: "neutral.400",
      bg: "neutral.0",
    },
    _focus: {
      borderColor: "p.500",
      boxShadow: "focusRing",
      outline: "none",
    },
    _focusVisible: {
      borderColor: "p.500",
      boxShadow: "focusRing",
      outline: "none",
    },
    _disabled: {
      bg: "neutral.100",
      color: "neutral.500",
      borderColor: "neutral.200",
      cursor: "not-allowed",
      _hover: {
        bg: "neutral.100",
        borderColor: "neutral.200",
      },
    },
  },
});

const Select = forwardRef<SelectProps, "select">(
  ({ size = "md", children, ...rest }, ref) => {
    const s = SIZE_STYLES[size];
    return (
      <Box position="relative" width="100%">
        <StyledSelect
          ref={ref}
          px={s.px}
          py={s.py}
          pr="36px" // room for the chevron
          fontSize={s.fontSize}
          {...rest}
        >
          {children}
        </StyledSelect>
        <Box
          position="absolute"
          right="md" // 12px
          top="50%"
          transform="translateY(-50%)"
          color="neutral.500"
          pointerEvents="none"
          display="inline-flex"
          alignItems="center"
          aria-hidden
        >
          <TbChevronDown size={12} strokeWidth={2.5} />
        </Box>
      </Box>
    );
  },
);

export default Select;
